import { Span, SpanLike } from '@paragon/geometry-library';
import { notNullish } from '@paragon/utilities';

/** Represents a truss envelope's points and overhangs in 1-dimensional space. */
export class EnvelopeSpan implements SpanLike<EnvelopeSpan> {
  readonly span: Span;
  readonly overhangSpan: Span;

  private constructor(span: Span, overhangSpan: Span) {
    this.span = span;
    this.overhangSpan = overhangSpan;
  }

  public static fromSpan(span: Span) {
    return new EnvelopeSpan(span, span);
  }

  public intersect(other: EnvelopeSpan): EnvelopeSpan | null {
    const intersected = this.span.intersect(other.span);
    const intersectedOverhang = this.overhangSpan.intersect(other.overhangSpan);

    return intersected && intersectedOverhang && new EnvelopeSpan(intersected, intersectedOverhang);
  }

  public splitAt(distance: number): EnvelopeSpan[] {
    const split = this.span.splitAt(distance);
    const splitOverhangs = this.overhangSpan.splitAt(distance);
    return split
      .map((splitPiece) => {
        const overhangPiece = splitOverhangs.find((overhangPiece) => splitPiece.overlaps(overhangPiece));
        return overhangPiece && new EnvelopeSpan(splitPiece, overhangPiece);
      })
      .filter(notNullish);
  }

  public growIntoAdjacentOverhangSpan(span: { onLeft?: MaybeSpan; onRight?: MaybeSpan }) {
    const newOverhangSpan = this.overhangSpan.growIntoAdjacentSpan(span);
    return new EnvelopeSpan(this.span, newOverhangSpan);
  }

  public growIntoAdjacentOverhangSpans = this.growIntoAdjacentOverhangSpan;

  public get leftOverhang(): number {
    return this.span.start - this.overhangSpan.start;
  }

  public get rightOverhang(): number {
    return this.overhangSpan.end - this.span.end;
  }

  public get start(): number {
    return this.span.start;
  }

  public get end(): number {
    return this.span.end;
  }
}

type MaybeSpan = Span | null | undefined;
